import { Component, Injector, Input, OnInit, ChangeDetectionStrategy } from "@angular/core";
import { finalize, takeUntil } from "rxjs";
import { AbstractComponent } from "../../../commons/abstract.component";
import { CampaignService } from "../../../services/campaign.service";
import { ChannelService } from "../../../services/channel.service";
import { ContactService } from "../../../services/contact.service";
import { PaymentService } from "../../../services/apps/payment.service";
import { UserService } from "../../../services/user.service";
import { WebhookService } from "../../../services/webhook.service";
import { BillingDetails } from "../../../models/billing.types";

@Component({
    selector: 'billing-details-component',
    templateUrl: './billing-details.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class BillingDetailsComponent extends AbstractComponent implements OnInit {

  @Input() companyId: string;

  error: string | null = null;
  billingDetails: BillingDetails | null = null;
  totalUsers: number = 0;
  totalChannels: number = 0;
  totalChannelsPlan: number = 0;
  totalContacts: number = 0;
  totalCampaigns: number = 0;
  totalWebhooks: number = 0;
  totalPublicApi: number = 0;

  constructor(
    injector: Injector,
    private paymentService: PaymentService,
    private userService: UserService,
    private channelService: ChannelService,
    private contactService: ContactService,
    private campaignService: CampaignService,
    private webhookService: WebhookService,
  ) {
    super(injector);
  }

  ngOnInit(): void {
    const targetCompanyId = this.companyId || this.getIDCurrentCompany();
    if (targetCompanyId) {
      this.loadBillingDetails(targetCompanyId);
      this.loadUsers(targetCompanyId);
      this.loadChannels(targetCompanyId);
      this.loadContacts(targetCompanyId);
      this.loadCampaigns(targetCompanyId);
      this.loadWebhooks(targetCompanyId);
      this.loadPublicApi(targetCompanyId);
    }
  }

  async loadBillingDetails(companyId: string): Promise<void> {
    this.loadingContent = true;
    this.paymentService.getBillingDetails(companyId)
      .pipe(takeUntil(this.destroy$), finalize(() => this.loadingContent = false))
      .subscribe({
        next: (data) => {
          this.billingDetails = data;
          this.updateUsageData(data);
        },
      });
  }

  async loadUsers(companyId: string): Promise<void> {
    this.userService.getCountTotalUsers(companyId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (count) => {
          this.totalUsers = count;
        }
      });
  }

  async loadChannels(companyId: string): Promise<void> {
    this.channelService.getCountTotalChannels(companyId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (count) => {
          this.totalChannels = count;
        }
      });
  }

  async loadContacts(companyId: string): Promise<void> {
    this.contactService.getCountTotalContact(companyId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (count) => {
          this.totalContacts = count;
        }
      });
  }

  async loadCampaigns(companyId: string): Promise<void> {
    this.campaignService.getCountTotalCampaing(companyId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (count) => {
          this.totalCampaigns = count;
        }
      });
  }

  async loadWebhooks(companyId: string): Promise<void> {
    this.webhookService.getCountTotalWebhooks(companyId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (count) => {
          this.totalWebhooks = count;
        }
      });
  }

  async loadPublicApi(companyId: string): Promise<void> {
    this.webhookService.getEventsByUUID(companyId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (count) => {
          this.totalPublicApi = count as any;
        }
      });
  }

  updateUsageData(billingData: BillingDetails): void {
    if (!billingData?.currentPlan?.benefits) return;

    const benefits = billingData.currentPlan.benefits;

    const totalChannels =
      (benefits.maxCloudAPI || 0) +
      (benefits.maxZAPI || 0) +
      (benefits.maxInstagram || 0) +
      (benefits.maxMessenger || 0);

    this.totalChannelsPlan = totalChannels;
  }

  getUsagePercentage(used: number, total: number): number {
    if (total <= 0) return 0;
    const percentage = (used / total) * 100;
    return Math.min(100, Math.max(0, percentage));
  }

  getProgressBarClass(percentage: number): string {
    if (percentage < 50) return 'bg-success';
    if (percentage < 80) return 'bg-warning';
    return 'bg-danger';
  }

  formatCurrency(amount: number, currency: string = 'BRL'): string {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: currency
    }).format(amount / 100);
  }

  formatDate(date: Date | string): string {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    return dateObj?.toLocaleDateString('pt-BR');
  }

  getStatusBadgeClass(status: string): string {
    switch (status) {
      case 'active':
      case 'trialing':
        return 'bg-success';
      case 'past_due':
      case 'incomplete':
        return 'bg-warning';
      case 'canceled':
      case 'unpaid':
        return 'bg-danger';
      default:
        return 'bg-secondary';
    }
  }

  getStatusText(status: string): string {
    const statusMap: { [key: string]: string } = {
      'active': this.t('shared.billingDetails.statusActive'),
      'trialing': this.t('shared.billingDetails.statusTrialing'),
      'past_due': this.t('shared.billingDetails.statusPastDue'),
      'canceled': this.t('shared.billingDetails.statusCanceled'),
      'unpaid': this.t('shared.billingDetails.statusUnpaid'),
      'incomplete': this.t('shared.billingDetails.statusIncomplete'),
      'paid': this.t('shared.billingDetails.statusPaid'),
      'open': this.t('shared.billingDetails.statusOpen'),
      'void': this.t('shared.billingDetails.statusVoid'),
      'uncollectible': this.t('shared.billingDetails.statusUncollectible')
    };
    return statusMap[status] || status;
  }

  getPeriodText(interval: string, interval_count: number): string {
    if (interval === 'month') {
      return interval_count === 1 ? this.t('shared.billingDetails.periodMonthly') : this.t('shared.billingDetails.periodMonths', { count: interval_count });
    } else if (interval === 'year') {
      return interval_count === 1 ? this.t('shared.billingDetails.periodAnnual') : this.t('shared.billingDetails.periodYears', { count: interval_count });
    }
    return interval;
  }

  timestampToDate(timestamp: number): Date {
    return new Date(timestamp * 1000);
  }

  async generateLinkPlan(customerId: string): Promise<void> {
    this.loadingContent = true;
    this.paymentService.getDetailsHistory(customerId)
      .pipe(takeUntil(this.destroy$), finalize(() => this.loadingContent = false))
      .subscribe({
        next: (link) => {
          window.open(link, '_blank');
        },
        error: (error) => {
          console.error('Erro ao gerar link de pagamento:', error);
          this.loadingContent = false;
        }
      });
  }
}
